// convert to hook

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { setLicenceValidity } from "../reducers/licence";
import { store } from "../ProviderApp";
import _ from "lodash";

// Standard API response wrapper type
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

// Typed wrapper functions - defaults to any for backward compatibility
// Use explicit types for strict checking: axiosWrapper.post<MyResponse>(...)
export const typedAxios = {
  post<TResponse = any, TRequest = any>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<TResponse>> {
    return axios.post<TResponse>(url, data, config);
  },

  get<TResponse = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<TResponse>> {
    return axios.get<TResponse>(url, config);
  },

  put<TResponse = any, TRequest = any>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<TResponse>> {
    return axios.put<TResponse>(url, data, config);
  },

  delete<TResponse = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<TResponse>> {
    return axios.delete<TResponse>(url, config);
  },

  patch<TResponse = any, TRequest = any>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<TResponse>> {
    return axios.patch<TResponse>(url, data, config);
  },
};

axios.interceptors.request.use(
  (config) => {
    const returnedConfig = {
      ...config,
      onUploadProgress: (progressEvent: any) => {
        if (config.onUploadProgress) {
          (config.onUploadProgress as any)(
            Math.floor((progressEvent.loaded * 100) / progressEvent.total),
            progressEvent.loaded,
          );
        }
      },
    };
    returnedConfig.withCredentials = true;
    return returnedConfig;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      _.get(error, "response.status") == "401" &&
      !["/googleLoginCallback", "/95789", "/signup", "/thank-you", "/pdfExport", "/"].includes(window.location.pathname)
    ) {
      localStorage.removeItem("isLogedIn");
      const errorMessage = error.response.data.errorMessage;

      const searchParams = new URLSearchParams();
      searchParams.set("redirectUrl", encodeURIComponent(window.location.pathname + window.location.search));
      if (errorMessage) {
        searchParams.set("errorMessage", encodeURIComponent(errorMessage));
      }

      const redirectUrl = `/?${searchParams.toString()}${window.location.hash}`;
      window.location.href = redirectUrl;
    } else if (_.get(error, "response.status") == "403" && _.get(error, "response.data.errorCode") === 1001) {
      console.log("licence expired");
      store.dispatch(setLicenceValidity(false));
    } else if (_.get(error, "response.status") == "429") {
      return false;
    }
    return Promise.reject(error);
  },
);

// Default export uses typed wrapper - this will error if response type is not specified
export default typedAxios;

// Named exports for flexibility
export const axiosWrapper = typedAxios;
export { axios as rawAxios }; // Use this only when you need raw axios (e.g., for interceptor config)
